import { Component, ChangeDetectionStrategy, input, output, signal, computed, effect } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { PropertyEditor } from '../property-editor/property-editor';

@Component({
  selector: 'app-properties-edit',
  standalone: true,
  imports: [
    FormsModule,
    PropertyEditor
],
  templateUrl: './properties-edit.html',
  styleUrl: './properties-edit.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PropertiesEdit {
  readonly schemas = input<Array<{ [key: string]: any }>>([]);
  readonly values = input<{ [key: string]: any }>({});
  readonly disabled = input<boolean>(false);
  /** Список ключей, которые должны быть заполнены (не null/empty). Из
   *  OpenApiPropertiesSchema.required. Плюс любое поле с nullable !== true
   *  тоже считается обязательным по факту — редактор не даёт «выключить»
   *  такой value через null-toggle. */
  readonly required = input<string[]>([]);
  readonly valuesChange = output<{ [key: string]: any }>();
  /** Меняется когда меняется общая валидность формы (все required заполнены
   *  корректно). Родитель может дисейблить submit-кнопку. */
  readonly validChange = output<boolean>();

  /** Merged schema, sorted by x-order. Derived from `schemas`. */
  readonly combinedSchema = computed<{ [key: string]: any }>(() => {
    const merged: { [key: string]: any } = {};
    for (const schema of this.schemas() || []) {
      if (schema) Object.assign(merged, schema);
    }
    return merged;
  });

  readonly propertyKeys = computed<string[]>(() => {
    const merged = this.combinedSchema();
    return Object.keys(merged).sort((a, b) => {
      const orderA = merged[a]['x-order'] !== undefined ? merged[a]['x-order'] : 999;
      const orderB = merged[b]['x-order'] !== undefined ? merged[b]['x-order'] : 999;
      return orderA - orderB;
    });
  });

  /** Inputs merged with schema defaults, the source of truth for the UI. */
  readonly localValues = signal<{ [key: string]: any }>({});

  /** Все required-имена: явные из `required()` input + поля с nullable !== true
   *  (не-nullable по OpenAPI = обязательное). */
  private readonly requiredKeys = computed<Set<string>>(() => {
    const set = new Set<string>(this.required() ?? []);
    const merged = this.combinedSchema();
    for (const key of Object.keys(merged)) {
      if (merged[key]?.nullable !== true) set.add(key);
    }
    return set;
  });

  /** Валидность формы — все required заполнены не-пустым значением. */
  readonly isValid = computed<boolean>(() => {
    const required = this.requiredKeys();
    const values = this.localValues();
    const merged = this.combinedSchema();
    for (const key of required) {
      const schema = merged[key];
      if (!schema) continue;
      if (this.isEmpty(values[key], schema.type)) return false;
    }
    return true;
  });

  private isEmpty(value: any, type?: string): boolean {
    if (value === null || value === undefined) return true;
    if (type === 'string' && typeof value === 'string' && value.trim() === '') return true;
    if (type === 'array' && Array.isArray(value) && value.length === 0) return true;
    return false;
  }

  constructor() {
    // Re-seed localValues whenever schemas or values change. If the seeding
    // adds entries that weren't in the input `values` (i.e., defaults from
    // the schema), emit them back so the parent persists them on save.
    effect(() => {
      const incoming = this.values() || {};
      const merged = this.combinedSchema();
      const keys = this.propertyKeys();
      const seeded: { [key: string]: any } = { ...incoming };
      let addedFromSchema = false;
      for (const key of keys) {
        if (seeded[key] !== undefined) continue;
        const schema = merged[key];
        if (!schema) continue;
        seeded[key] = schema.default !== undefined
          ? schema.default
          : this.getDefaultForType(schema.type);
        addedFromSchema = true;
      }
      this.localValues.set(seeded);
      if (addedFromSchema) {
        // Defer to a microtask so we don't emit during the same CD cycle
        // that's currently flowing input bindings into us.
        Promise.resolve().then(() => this.valuesChange.emit({ ...seeded }));
      }
    });

    // Пробрасываем validity наружу — родитель дисейблит submit если false.
    effect(() => {
      const v = this.isValid();
      Promise.resolve().then(() => this.validChange.emit(v));
    });
  }

  getDefaultForType(type?: string): any {
    switch (type) {
      case 'string': return '';
      case 'integer':
      case 'number': return 0;
      case 'boolean': return false;
      case 'array': return [];
      case 'object': return {};
      default: return null;
    }
  }

  onPropertyChange(key: string, value: any): void {
    const next = { ...this.localValues(), [key]: value };
    this.localValues.set(next);
    this.valuesChange.emit(next);
  }

  getPropertyTitle(key: string, schema: any): string {
    if (schema?.title) return schema.title;
    return key
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .trim()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  getPropertySubtitle(schema: any): string | undefined {
    return schema?.description;
  }
}
